import { useEffect, useMemo, useRef, useState } from "react";
import { useGameStore } from "../state/useGameStore";
import {
  PROMPT_RETIRE_STRIKES,
  STAGE_ORDER,
  UPGRADES,
} from "../config/game";

function useProgress(clickCount, unlockedIds) {
  return useMemo(() => {
    const next = UPGRADES.find((u) => !unlockedIds.includes(u.id));
    if (!next) {
      return { progress: 1, target: clickCount, next: null };
    }
    const prevThreshold =
      UPGRADES.filter((u) => unlockedIds.includes(u.id)).at(-1)?.threshold ?? 0;
    const span = Math.max(1, next.threshold - prevThreshold);
    const current = Math.max(0, clickCount - prevThreshold);
    return {
      progress: Math.min(1, current / span),
      target: next.threshold,
      next,
    };
  }, [clickCount, unlockedIds]);
}

function stageLabel(phase, unlockedIds) {
  if (phase === "heap") return "INSPECT";
  for (const id of STAGE_ORDER) {
    if (unlockedIds.includes(id)) return id.toUpperCase();
  }
  return "FORM";
}

function ScoreFloat() {
  const pulseToken = useGameStore((s) => s.pulseToken);
  const lastGain = useGameStore((s) => s.lastGain);
  const [float, setFloat] = useState(null);
  const lastToken = useRef(0);

  useEffect(() => {
    if (pulseToken === lastToken.current) return;
    lastToken.current = pulseToken;
    if (!lastGain) return;
    const id = pulseToken;
    setFloat({ id, value: lastGain });
    const t = window.setTimeout(() => {
      setFloat((f) => (f?.id === id ? null : f));
    }, 700);
    return () => window.clearTimeout(t);
  }, [pulseToken, lastGain]);

  if (!float) return null;
  const sign = float.value > 0 ? "+" : "";
  return (
    <span className="hud__score-float" aria-hidden="true">
      {sign}
      {float.value.toLocaleString()}
    </span>
  );
}

export function HUD() {
  const phase = useGameStore((s) => s.phase);
  const score = useGameStore((s) => s.score);
  const clickCount = useGameStore((s) => s.clickCount);
  const multiplier = useGameStore((s) => s.multiplier);
  const unlockedIds = useGameStore((s) => s.unlockedIds);
  const coarsePointer = useGameStore((s) => s.coarsePointer);
  const climaxActive = useGameStore((s) => s.climaxActive);
  const boostOfferActive = useGameStore((s) => s.boostOfferActive);
  const muted = useGameStore((s) => s.muted);
  const setMutedPref = useGameStore((s) => s.setMutedPref);
  const strike = useGameStore((s) => s.strike);
  const surgeUntil = useGameStore((s) => s.surgeUntil);
  const { progress, target, next } = useProgress(clickCount, unlockedIds);

  if (phase === "intro") return null;

  const stage = stageLabel(phase, unlockedIds);
  const promptRetired =
    clickCount >= PROMPT_RETIRE_STRIKES || unlockedIds.length > 0;
  const surgeLive = surgeUntil > performance.now();
  const unlockedNames = UPGRADES.filter((u) =>
    unlockedIds.includes(u.id)
  ).map((u) => u.name);

  return (
    <div className={`hud ${phase === "heap" ? "hud--heap" : ""}`}>
      <header className="hud__brand">
        <span className="hud__mark">HEAP</span>
        <span className="hud__stage">{stage}</span>
      </header>

      <div className="hud__score" aria-live="polite">
        <span className="hud__score-label">MASS</span>
        <span className="hud__score-value">{score.toLocaleString()}</span>
        <ScoreFloat />
      </div>

      <button
        type="button"
        className={`hud__mute ${muted ? "is-on" : ""}`}
        onClick={() => setMutedPref(!muted)}
        aria-pressed={muted}
        aria-label={muted ? "Unmute audio" : "Mute audio"}
      >
        {muted ? "Sound off" : "Sound on"}
      </button>

      {phase === "play" && (
        <>
          <p className="hud__prompt">
            {!promptRetired
              ? "CLICK TO FORM"
              : next
                ? `Next · ${next.label}`
                : "Vein complete · keep forming"}
          </p>

          <div
            className="hud__rail"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            aria-label="Progress to next unlock"
          >
            <div className="hud__rail-meta">
              <span>STRIKES</span>
              <span>{next ? `${clickCount}/${target}` : "MAX"}</span>
            </div>
            <div className="hud__rail-track">
              <div
                className={`hud__rail-fill ${unlockedIds.length ? "is-hot" : ""}`}
                style={{ transform: `scaleX(${progress})` }}
              />
            </div>
            <div className="hud__rail-meta hud__rail-meta--foot">
              <span>{next ? next.name : "COMPLETE"}</span>
              <span className="hud__mult">
                ×{multiplier}
                {surgeLive ? " · SURGE" : ""}
              </span>
            </div>
            <p className="hud__rail-cal">CAL · strike count toward unlocks</p>
          </div>

          <ul className="hud__skills" aria-label="Unlock stages">
            {UPGRADES.map((u) => {
              const on = unlockedIds.includes(u.id);
              return (
                <li
                  key={u.id}
                  className={`hud__skill ${on ? "is-on" : ""}`}
                  aria-label={`${u.name}: ${u.label}${on ? ", unlocked" : ", locked"}`}
                >
                  <span className="hud__skill-name">{u.name}</span>
                  <span className="hud__skill-label">{u.label}</span>
                </li>
              );
            })}
          </ul>

          {coarsePointer && !climaxActive && !boostOfferActive && (
            <button
              type="button"
              className="hud__strike"
              onClick={strike}
              aria-label="Strike the central mass"
            >
              <span className="cta__label">Strike</span>
            </button>
          )}
        </>
      )}

      {phase === "heap" && (
        <div className="hud__heap">
          <p className="hud__heap-copy">
            Your Heap below. Matter settled from every strike.
          </p>
          <dl className="hud__heap-stats">
            <div>
              <dt>MASS</dt>
              <dd>{score.toLocaleString()}</dd>
            </div>
            <div className="hud__heap-unlocks">
              <dt>UNLOCKS</dt>
              <dd>
                {unlockedNames.length ? unlockedNames.join(" · ") : "None yet"}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
